import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Form, Radio, Space } from 'antd'
import { CustomForm } from 'components/Forms/CustomForm'
import cn from 'classnames'
import { Input } from 'components/Input'
import { Select } from 'components/Select'
import { setLoading } from 'redux/store/common/slice'
import { fetchContractCategoriesForRfp } from 'pages/CRUDUser/api'
import { getUser } from 'redux/store/user/getters'
import { createRFP, updateRFP } from 'features/RFP/RFPDetails/api'
import { LABELS, PLACEHOLDERS } from 'constants/txt'
import { getUserCommunity, validateMessages } from 'helper/common'
import { TRFPData } from 'features/RFP/RFPDetails/types'
import { TOption } from 'components/Select/types'
import {
  TFormRFPCreationFormProps,
  TFormRFPCreationFormFields,
  TFormRFPCreationForm
} from './forms.rfpCreation.d'
import './forms.rfpCreation.scss'
import { formatOptionNameValue } from '../../helper/optionsFormatters'
import { ROLES } from 'features/Permission'
import { Reassign } from 'features/Reassign/ReassignPopup'
import { routes } from 'router'
import { Typography, TYPOGRAPHY_WEIGHT } from '../Typography'
import {
  initialRfp,
  initialRfpCommons,
  PARTICIPATION_TYPE_OPTIONS,
  RFP_TYPE_OPTIONS
} from '../../features/RFP/RFPDetails/constants'
import { FieldWithLabel } from '../FieldWithLabel/FieldWithLabel'
import { RFP_STATUSES } from '../../features/RFP/constants'
import { useNavigate } from 'react-router'
import useRouter from '../../hooks/useRouter'

export const RFPCreation = ({
  className,
  onValueChange,
  callback,
  data,
  form,
  setDiscard
}: TFormRFPCreationFormProps): JSX.Element => {
  const [reassignRfpData, setRfpData] = useState({} as TRFPData)
  const [isOpenReassign, setIsOpenReassign] = useState(false)
  const [contractCategories, setContractCategories] = useState<TOption[]>([])
  const [fields, setFields] = useState<TFormRFPCreationFormFields>([])
  const dispatch = useDispatch()
  const user = useSelector(getUser)
  const { id } = useParams()
  const { query } = useRouter()
  const navigate = useNavigate()
  const isCommons = user?.role.includes('commons')
  const creationType = id === 'new' ? query.typeCreation : data.creation_type

  useEffect(() => {
    if (getUserCommunity(user) || user.commons) {
      dispatch(setLoading(true))
      getOptions().finally(() => dispatch(setLoading(false)))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])
  useEffect(() => {
    if (data) {
      const formattedData = {}
      for (const key in data) {
        if (Array.isArray(data[key])) {
          formattedData[key] = data[key].map(formatOptionNameValue)
        } else {
          formattedData[key] = data[key]
        }
      }
      const fields = Object.keys(formattedData).map((i) => ({
        name: i,
        value: formattedData[i]
      }))
      setFields(fields)
    }
  }, [data])
  const getOptions = async () => {
    await fetchContractCategoriesForRfp(creationType).then((resp) => {
      if (!resp.data?.results) {
        return
      }

      setContractCategories(resp.data.results.map(formatOptionNameValue))
    })
    // code for Contract Pipeline specific RFP creation
    // it predefines some values - HS list and CC in the pickers
    // it was put right after fetching of picker items to automatically setup necessary values
    if (query.cpr_uuid && id === 'new') {
      const initFieldsValue: Partial<TFormRFPCreationForm> = {}

      // Setting CC based on data gat from Contract Pipeline page
      if (query.contract_category_id && query.contract_category_name) {
        initFieldsValue.contract_categories = [
          {
            value: query.contract_category_id,
            label: query.contract_category_name
          }
        ]
      }

      // setting initial values for RFP
      if (Object.keys(initFieldsValue).length) {
        setDiscard(true)
        form.setFieldsValue(initFieldsValue)
      }
    }
  }
  const handleSubmitForm = async (formData) => {
    dispatch(setLoading(true))
    const rfpData: TRFPData = {
      name: formData.name,
      contract_categories: formData.contract_categories
        ? typeof formData.contract_categories[0] === 'object'
          ? formData.contract_categories.map((i) => i.value)
          : formData.contract_categories
        : undefined,
      creation_type:
        id === 'new'
          ? query.typeCreation
          : user?.commons && data.status !== RFP_STATUSES.IN_CS_QUEUE
          ? // TODO: return back OM_LIFELINE creation when BE is ready
            // https://leverxeu.atlassian.net/browse/ECOG01-12387
            // ? LED_BY_VALUE.OM_LIFELINE
            'qsight_community'
          : formData.creation_type
    }
    if (!user.commons) {
      rfpData['participation_type'] = formData.participation_type
    }
    if (query.cpr_uuid) {
      rfpData['contract_pipeline'] = query.cpr_uuid
    }
    try {
      setDiscard(false)
      if (id === 'new') {
        if (user.role === ROLES.COMMUNITY_PRESIDENT) {
          setRfpData({ ...rfpData, creator: user.uuid })
          setIsOpenReassign(true)
          return
        }
        const resp = await createRFP({ ...rfpData, creator: user.uuid })
        if (resp?.data?.uuid) {
          navigate(`${routes.bidding}${routes.rfp}/${resp?.data?.uuid}/`, {
            replace: true
          })
        }
      } else {
        await updateRFP(
          id as string,
          rfpData,
          'RFP has been successfully saved.'
        ).then(() => {
          if (callback) {
            callback()
          }
        })
      }
    } finally {
      dispatch(setLoading(false))
    }
  }
  const canEditRfpType =
    (user.role !== ROLES.COMMUNITY_PRESIDENT &&
      !user.commons &&
      (id === 'new' ||
        data.status === RFP_STATUSES.CREATED ||
        data.status === RFP_STATUSES.WAITING_FOR_ASSIGNMENT)) ||
    (data.status === RFP_STATUSES.IN_CS_QUEUE &&
      user?.commons &&
      data.creation_type !== 'commons')

  return (
    <>
      <CustomForm<TFormRFPCreationForm>
        onFinish={handleSubmitForm}
        fields={fields}
        form={form}
        initialValues={isCommons ? initialRfpCommons : initialRfp}
        onValuesChange={(_, all) => {
          onValueChange(all)
        }}
        validateMessages={validateMessages}
        className={cn('rfp-creation-form', className)}
        id="rfp-creation"
      >
        <div className="row two-columns">
          <Input
            propsItem={{
              name: 'name',
              label: LABELS.NAME,
              rules: [
                {
                  max: 100,
                  required: true
                }
              ]
            }}
            propsInput={{
              showCount: true,
              maxLength: 100
            }}
          />
        </div>
        <div className="row mt-24">
          <Select.Multi
            options={contractCategories}
            propsItem={{
              name: 'contract_categories',
              label: LABELS.CONTRACT_CATEGORIES,
              rules: [{ required: true }]
            }}
            propsSelect={{
              placeholder: PLACEHOLDERS.PLEASE_SELECT,
              disabled: data.status === RFP_STATUSES.IN_CS_QUEUE
            }}
          />
        </div>
        {id !== 'new' && data.status !== RFP_STATUSES.CREATED && !user.commons && (
          <div className="row two-columns mt-24">
            {!canEditRfpType && (
              <FieldWithLabel
                title={LABELS.PARTICIPATION_TYPE}
                className="participation-type-view"
              >
                {
                  PARTICIPATION_TYPE_OPTIONS.find(
                    (i) => i.value === data?.participation_type
                  )?.label
                }
              </FieldWithLabel>
            )}
            <FieldWithLabel title={LABELS.RFP_TYPE} className="rfp-type-view">
              {
                RFP_TYPE_OPTIONS.find((i) => i.value === data?.creation_type)
                  ?.label
              }
            </FieldWithLabel>
          </div>
        )}
        {(id === 'new' || data.status === RFP_STATUSES.CREATED) &&
          !user.commons && (
            <div className="mt-24 row two-columns rfp-type-wrapper participants">
              <Form.Item
                label={LABELS.PARTICIPATION_TYPE}
                name="participation_type"
                colon={false}
              >
                <Radio.Group className={cn('radio-group-default', 'mt-16')}>
                  <Space direction="vertical">
                    {PARTICIPATION_TYPE_OPTIONS.map((i) => (
                      <Radio key={i.value} value={i.value}>
                        <Typography.Body1
                          weight={TYPOGRAPHY_WEIGHT.NORMAL}
                          className={cn('run-new-step-option')}
                        >
                          {i.label}
                        </Typography.Body1>
                      </Radio>
                    ))}
                  </Space>
                </Radio.Group>
              </Form.Item>
            </div>
          )}
      </CustomForm>
      {id === 'new' && user.role === ROLES.COMMUNITY_PRESIDENT && (
        <Reassign
          isCreatingRFP
          rfpData={reassignRfpData}
          isRFP
          isReassignPopupOpen={isOpenReassign}
          setIsReassignPopupOpen={setIsOpenReassign}
          ownerRole={user.role}
          ownerName={`${user.first_name} ${user.last_name}`}
        />
      )}
    </>
  )
}
